import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, BarChart2, Clock, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
} from "recharts";

const ACCENT = "#EC4899";

function fmtH(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `${Number(n).toFixed(2)}h`;
}
function fmtHRound(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `${Math.round(Number(n))}h`;
}

export default function MoldHoursBreakdownCards({ showMexico = false, dept = 'MOLD' }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ categories: [], overtime: [] });
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/hours-breakdown`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mold-hours-breakdown">
      <HoursByCategoryCard
        filter={filter} onFilterChange={setFilter}
        loading={loading} categories={data.categories || []}
        dept={dept}
      />
      <OvertimeDetailsCard
        loading={loading}
        overtime={data.overtime || []}
        filter={filter}
        dept={dept}
      />
    </div>
  );
}

function HoursByCategoryCard({ filter, onFilterChange, loading, categories, dept = "MOLD" }) {
  const max = categories.reduce((m, c) => Math.max(m, c.hours), 0);
  // Round up to a nice tick (700, 1400, 2100, 2800...)
  const niceMax = Math.ceil((max || 100) / 700) * 700;

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="hours-by-category-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BarChart2 size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">Hours by Category</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">
          {categories.length} categories
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Phase = {dept}, grouped by Category Name
      </p>

      <div className="mb-4">
        <DateFilter value={filter} onChange={onFilterChange} />
      </div>

      {loading && (
        <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}
      {!loading && categories.length === 0 && (
        <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="hours-category-empty">
          No hours in range
        </div>
      )}
      {!loading && categories.length > 0 && (
        <div className="h-[420px]" data-testid="hours-category-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <BarChart
              data={categories}
              layout="vertical"
              margin={{ top: 5, right: 50, left: 30, bottom: 5 }}
            >
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number" domain={[0, niceMax]}
                ticks={Array.from({ length: 5 }, (_, i) => Math.round((niceMax / 4) * i))}
                stroke="#94A3B8" tick={{ fontSize: 10 }}
              />
              <YAxis
                type="category" dataKey="name" stroke="#94A3B8"
                tick={{ fontSize: 10 }} width={110}
              />
              <Tooltip
                cursor={{ fill: "rgba(236,72,153,0.05)" }}
                contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                labelStyle={{ color: "#94A3B8" }}
                formatter={(v) => [`${Number(v).toFixed(1)}h`, "Hours"]}
              />
              <Bar dataKey="hours" fill={ACCENT} radius={[0, 2, 2, 0]} barSize={18}>
                <LabelList
                  dataKey="hours"
                  position="right"
                  formatter={(v) => fmtHRound(v)}
                  style={{ fill: "#94A3B8", fontSize: 10, fontFamily: "monospace" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function OvertimeDetailsCard({ loading, overtime, filter, dept = "MOLD" }) {
  const [expanded, setExpanded] = useState(null); // employee name or null
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return overtime;
    const q = search.toLowerCase();
    return overtime.filter((o) => (o.employee || "").toLowerCase().includes(q));
  }, [overtime, search]);

  const onToggle = (emp) => {
    if (expanded === emp) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(emp);
    setDetail(null);
    setDetailLoading(true);
    const p = { preset: filter.preset, employee: emp };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    api.get(`/departments/${dept}/hours-breakdown/employee`, { params: p })
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setDetailLoading(false));
  };

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="overtime-details-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">{dept === "FRAME" ? "Frame" : dept === "GFRC" ? "GFRC" : dept === "FINISH" ? "Finish" : dept === "FIBERGLASS" ? "Fiberglass" : "Mold"} Dept Hours Summary</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">
          {search ? `${filtered.length} / ${overtime.length}` : `${overtime.length}`} employees
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {dept === "FRAME" ? "Frame" : dept === "GFRC" ? "GFRC" : dept === "FINISH" ? "Finish" : dept === "FIBERGLASS" ? "Fiberglass" : "Mold"} Hours Summary — tap a name to see daily breakdown
      </p>

      <div className="relative mb-4" data-testid="overtime-search-wrap">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="overtime-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employee…"
          className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#EC4899]/60"
        />
      </div>

      {loading && (
        <div className="h-[440px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}
      {!loading && overtime.length === 0 && (
        <div className="h-[440px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="overtime-empty">
          No overtime in range
        </div>
      )}
      {!loading && overtime.length > 0 && filtered.length === 0 && (
        <div className="h-[200px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="overtime-no-match">
          No employees match “{search}”
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="overtime-list">
          {filtered.map((o) => (
            <EmployeeRow
              key={o.employee}
              emp={o}
              expanded={expanded === o.employee}
              detail={expanded === o.employee ? detail : null}
              detailLoading={expanded === o.employee && detailLoading}
              onToggle={() => onToggle(o.employee)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function EmployeeRow({ emp, expanded, detail, detailLoading, onToggle }) {
  const high = emp.projected_overtime >= 10; // 10h+ projected OT = severe
  const ringBg = high ? "bg-red-900/30 border-red-700/40" : "bg-red-900/15 border-red-800/30";
  return (
    <div className={`border ${ringBg} transition-colors`} data-testid={`overtime-row-${emp.employee}`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors"
      >
        <AlertTriangle size={14} className="text-red-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {expanded
              ? <ChevronDown size={12} className="text-muted-foreground" />
              : <ChevronRight size={12} className="text-muted-foreground" />}
            <div className="font-heading font-semibold text-sm truncate">{emp.employee}</div>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {emp.days_worked} day{emp.days_worked === 1 ? "" : "s"} worked
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-bold tabular-nums text-white">
            {fmtH(emp.total_hours)}
          </div>
          <div className="text-[10px] text-red-400 font-mono mt-1">
            Proj: {fmtH(emp.projected_total)}
            {emp.projected_overtime > 0 ? ` (+${emp.projected_overtime.toFixed(2)}h OT)` : ""}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-red-800/30 bg-[#0A0B10]/60 px-4 py-3" data-testid={`overtime-detail-${emp.employee}`}>
          {detailLoading && (
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground py-4 text-center">
              Loading…
            </div>
          )}
          {!detailLoading && detail && (
            <>
              <div className="flex items-center gap-4 mb-3 text-[11px] font-mono">
                <span className="text-muted-foreground">Total:</span>
                <span className="text-white">{fmtH(detail.total_hours)}</span>
                <span className="text-muted-foreground">Regular:</span>
                <span className="text-emerald-400">{fmtH(detail.regular_hours)}</span>
                <span className="text-muted-foreground">OT:</span>
                <span className="text-red-400">{fmtH(detail.overtime_hours)}</span>
              </div>
              <div className="max-h-[260px] overflow-y-auto border border-border/40">
                <table className="w-full text-[11px]">
                  <thead className="bg-[#1A1C28] text-[9px] uppercase tracking-widest text-[#94A3B8] sticky top-0">
                    <tr>
                      <th className="py-1.5 px-2 text-left">Date</th>
                      <th className="py-1.5 px-2 text-left">Description</th>
                      <th className="py-1.5 px-2 text-left">Category</th>
                      <th className="py-1.5 px-2 text-left">Job</th>
                      <th className="py-1.5 px-2 text-right">Hrs</th>
                      <th className="py-1.5 px-2 text-right">OT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.rows.length === 0 && (
                      <tr><td colSpan={6} className="py-4 text-center text-muted-foreground uppercase tracking-widest text-[10px]">No entries</td></tr>
                    )}
                    {detail.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border/30">
                        <td className="py-1.5 px-2 font-mono text-muted-foreground">{r.date || "—"}</td>
                        <td className="py-1.5 px-2 truncate max-w-[160px]" title={r.description}>{r.description}</td>
                        <td className="py-1.5 px-2 text-muted-foreground">{r.category}</td>
                        <td className="py-1.5 px-2 font-mono text-muted-foreground">{r.job}</td>
                        <td className="py-1.5 px-2 font-mono text-right">{Number(r.hours).toFixed(2)}</td>
                        <td className="py-1.5 px-2 font-mono text-right text-red-400">
                          {r.overtime > 0 ? Number(r.overtime).toFixed(2) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
